import { LitElement, html, nothing } from "lit";
import { customElement, property, state, query } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
import "@phosphor-icons/webcomponents/PhSmiley";
import "@phosphor-icons/webcomponents/PhPaperPlaneTilt";
import { styles } from "./chat-message-input.css";

/**
 * Chat message input component with avatar, emoji button, and send button.
 *
 * Layout adapts as text grows:
 * - Single line: all elements vertically centered
 * - Multi-line: avatar stays top-left, buttons stay bottom-right
 *
 * @fires send-message - Dispatched when user sends a message
 * @fires emoji-toggle - Dispatched when emoji button is clicked
 *
 * @example
 * <chat-message-input
 *   placeholder="Send a message..."
 *   avatar-url="/path/to/avatar.jpg"
 *   @send-message=${this.handleSend}
 *   @emoji-toggle=${this.handleEmojiToggle}
 * ></chat-message-input>
 */
@customElement("chat-message-input")
export class ChatMessageInput extends LitElement {
  static override styles = styles;

  @query("textarea") private textareaElement!: HTMLTextAreaElement;

  /** Placeholder text for the input */
  @property({ type: String }) placeholder = "Send a message...";

  /** URL for the user's avatar image */
  @property({ type: String, attribute: "avatar-url" }) avatarUrl?: string;

  /** Whether input is disabled */
  @property({ type: Boolean }) disabled = false;

  /** Whether a message is currently being sent */
  @property({ type: Boolean }) sending = false;

  /** Whether to show the email notification toggle (creator only) */
  @property({ type: Boolean, attribute: "show-notify-toggle" }) showNotifyToggle = false;

  /** Label text for the notify toggle */
  @property({ type: String, attribute: "notify-label" }) notifyLabel = "Notify fans via email";

  /** Current input value */
  @state() private value = "";

  /** Whether email notification is enabled for the next message */
  @state() private notifyByEmail = false;

  /** Whether textarea has multiple lines */
  @state() private isSingleLine = true;

  private get canSend(): boolean {
    return this.value.trim().length > 0 && !this.disabled && !this.sending;
  }

  private handleInput(e: Event): void {
    const textarea = e.target as HTMLTextAreaElement;
    this.value = textarea.value;
    this.autoResize();
  }

  private handleKeyDown(e: KeyboardEvent): void {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      this.send();
    }
  }

  private autoResize(): void {
    const textarea = this.textareaElement;
    if (!textarea) return;

    // Reset height to measure natural height
    textarea.style.height = "auto";
    const scrollHeight = textarea.scrollHeight;

    // Check if content is single line (approximately 24px or less)
    this.isSingleLine = scrollHeight <= 28;

    // Set the height
    textarea.style.height = `${scrollHeight}px`;
  }

  private send(): void {
    if (!this.canSend) return;

    this.dispatchEvent(
      new CustomEvent("send-message", {
        detail: { message: this.value.trim(), notifyByEmail: this.notifyByEmail },
        bubbles: true,
        composed: true,
      }),
    );

    this.value = "";
    this.isSingleLine = true;
    this.notifyByEmail = false;

    // Reset textarea height
    if (this.textareaElement) {
      this.textareaElement.style.height = "auto";
    }

    // Restore focus after re-render triggered by value/state changes
    this.updateComplete.then(() => {
      this.textareaElement?.focus();
    });
  }

  private handleEmojiClick(): void {
    this.dispatchEvent(
      new CustomEvent("emoji-toggle", {
        bubbles: true,
        composed: true,
      }),
    );
  }

  /** Focus the textarea */
  public override focus(): void {
    this.textareaElement?.focus();
  }

  /** Clear the input value */
  public clear(): void {
    this.value = "";
    this.isSingleLine = true;
    if (this.textareaElement) {
      this.textareaElement.style.height = "auto";
    }
  }

  /** Insert text at cursor position (used for emoji insertion) */
  public insertText(text: string): void {
    this.value += text;
    this.updateComplete.then(() => {
      this.autoResize();
      this.focus();
    });
  }

  private handleNotifyToggle(e: Event): void {
    this.notifyByEmail = (e.target as HTMLInputElement).checked;
  }

  override render() {
    return html`
      <div class="input-wrapper">
        <div
          class=${classMap({
            container: true,
            "single-line": this.isSingleLine,
          })}
        >
          ${this.avatarUrl ? html`<img class="avatar" src=${this.avatarUrl} alt="" />` : nothing}

          <textarea
            class="message-input"
            .value=${this.value}
            placeholder=${this.placeholder}
            ?disabled=${this.disabled || this.sending}
            @input=${this.handleInput}
            @keydown=${this.handleKeyDown}
            rows="1"
          ></textarea>

          <div class="button-group">
            <button
              class="send-button"
              type="button"
              ?disabled=${!this.canSend}
              @click=${this.send}
              aria-label="Send message"
            >
              <ph-paper-plane-tilt size="20" weight="fill"></ph-paper-plane-tilt>
            </button>

            <button
              class="emoji-button"
              type="button"
              ?disabled=${this.disabled || this.sending}
              @click=${this.handleEmojiClick}
              aria-label="Open emoji picker"
            >
              <ph-smiley size="20" weight="bold"></ph-smiley>
            </button>
          </div>
        </div>
        ${this.showNotifyToggle
          ? html`
              <label class="notify-toggle">
                <input type="checkbox" .checked=${this.notifyByEmail} @change=${this.handleNotifyToggle} />
                <span class="notify-label">${this.notifyLabel}</span>
              </label>
            `
          : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "chat-message-input": ChatMessageInput;
  }
}
